<script setup lang="ts">
  import { ref } from 'vue';
  import { FormInput, IconImage, DatePicker } from '@/shared/ui';
  import { formatDateToPtBR } from '@/shared/lib';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import AddButton from '@/shared/assets/icons/AddButton.svg';
  import DeleteButton from '@/shared/assets/icons/DeleteButton.svg';
  import MinistranteCurriculoUploadField from './MinistranteCurriculoUploadField.vue';
  import type { ExternalCompanyInstructorRow } from '../../model/useOpenTrainingExternalCompanyInstructors';

  const props = defineProps<{
    row: ExternalCompanyInstructorRow;
    isFirst: boolean;
    isAddDisabled: boolean;
  }>();

  const emit = defineEmits<{
    'update:name': [value: string];
    'update:trainingDate': [date: Date | null];
    'select-curriculum-file': [file: File];
    'remove-curriculum-file': [];
    add: [];
    remove: [];
  }>();

  const isDatePickerOpen = ref(false);
  const testidPrefix = `open-training-external-company-${props.row.id}`;
</script>

<template>
  <div class="flex items-center justify-between gap-6 border-b border-gray-02 px-6 py-8">
    <div class="min-w-0 flex-1 flex flex-col gap-4">
      <div class="grid grid-cols-[minmax(240px,1fr)_182px] gap-6">
        <FormInput
          :id="`${testidPrefix}-name`"
          :model-value="row.name"
          label="Ministrante externo:"
          label-class="text-sm font-semibold text-gray-07"
          type="text"
          placeholder="Nome do Ministrante"
          input-class="h-11"
          :error="row.nameError ?? undefined"
          @update:model-value="emit('update:name', String($event))"
        />
        <div class="flex flex-col gap-2">
          <span
            :id="`${testidPrefix}-date-label`"
            class="text-sm font-semibold text-gray-07"
          >Data do treinamento:</span>
          <button
            type="button"
            :data-testid="`${testidPrefix}-date-trigger`"
            class="flex h-11 w-full items-center gap-2 rounded-lg border border-[var(--color-border-color)] bg-white-01 px-4 text-sm whitespace-nowrap focus:outline-none"
            :aria-labelledby="`${testidPrefix}-date-label`"
            aria-haspopup="dialog"
            :aria-expanded="isDatePickerOpen"
            @click="isDatePickerOpen = true"
          >
            <IconImage
              :src="CalendarIcon"
              alt="Calendário"
              class="h-5 w-5 shrink-0"
            />
            <span :class="row.trainingDate ? 'text-gray-07' : 'text-gray-03'">
              {{ row.trainingDate ? formatDateToPtBR(row.trainingDate) : 'Selecione uma data' }}
            </span>
          </button>
          <DatePicker
            :selected-date="row.trainingDate"
            :is-open="isDatePickerOpen"
            @update:selected-date="emit('update:trainingDate', $event)"
            @update:is-open="isDatePickerOpen = $event"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <span class="text-sm font-semibold text-gray-07">Currículo Ministrante:</span>
        <MinistranteCurriculoUploadField
          :testid="`${testidPrefix}-curriculum`"
          :curriculum-file-name="row.curriculumFileName"
          :upload-progress="row.uploadProgress"
          :upload-status="row.uploadStatus"
          @select-file="emit('select-curriculum-file', $event)"
          @remove-file="emit('remove-curriculum-file')"
        />
      </div>
    </div>

    <div class="flex h-[46px] shrink-0 items-center text-[var(--theme-secondary-ink)]">
      <button
        v-if="!isFirst"
        type="button"
        aria-label="Remover ministrante"
        class="flex cursor-pointer items-center justify-center"
        @click="emit('remove')"
      >
        <IconImage
          :src="DeleteButton"
          alt="Remover ministrante"
          class="h-6 w-6"
        />
      </button>
      <button
        v-if="isFirst"
        type="button"
        aria-label="Adicionar ministrante"
        :disabled="isAddDisabled"
        :aria-disabled="isAddDisabled"
        class="flex items-center justify-center"
        :class="isAddDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
        @click="emit('add')"
      >
        <IconImage
          :src="AddButton"
          alt="Adicionar ministrante"
          class="h-6 w-6"
        />
      </button>
    </div>
  </div>
</template>
